import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Chat } from 'src/types/sms'
import type { AppThunk } from '../store'
import { HTTP } from '../lib/axios'
import { LoadingPayload, SmsLoadingState } from '../types/common'
import { sortListByDate } from '../utils/common'

export interface SmsState {
  initiateChatNumber: string
  chats: Array<Chat>
  sms: Array<any>
  currentChat: string
  loading: SmsLoadingState
  errors: string
  stars: Array<any>
  starLoading: boolean
}
export const initialState: SmsState = {
  initiateChatNumber: '',
  chats: [],
  currentChat: '',
  sms: [],
  loading: { sms: false, chats: false, sending: false },
  errors: '',
  stars: [],
  starLoading: false
}

export const unreadSms = (state: SmsState) => {
  let unread = 0
  for (const chat of state.chats) {
    unread += chat.unreadMessages
  }
  return unread
}

const smsSlice = createSlice({
  name: 'sms',
  initialState,
  reducers: {
    setLoading: (state, { payload }: PayloadAction<LoadingPayload>) => {
      const clone = { ...state.loading, [payload.type]: payload.payload }
      state.loading = clone
    },
    setErrors: (state, { payload }: PayloadAction<string>) => {
      state.errors = payload
    },
    setChats: (state, { payload }: PayloadAction<Array<any>>) => {
      const existingChatIds: any = {}
      state.chats = sortListByDate(
        payload,
        'mostRecentMessageTime',
        true
      ).filter((chat) => {
        if (existingChatIds[chat.chatId]) {
          return false
        }
        existingChatIds[chat.chatId] = chat.id
        return true
      })
    },
    setChatRead: (state, { payload }: PayloadAction<string>) => {
      const newChats = [...state.chats]
      const chatIndex = newChats.findIndex((chat) => chat.id === payload)
      if (chatIndex > -1) {
        newChats[chatIndex].unreadMessages = 0
      }
      state.chats = newChats
    },
    setSms: (state, { payload }: PayloadAction<[]>) => {
      state.sms = payload
    },
    appendSms: (state, { payload }: PayloadAction<[]>) => {
      state.sms = [...state.sms, ...payload]
    },
    insertSms: (state: SmsState, { payload }: PayloadAction<any>) => {
      if (
        !state.sms.length ||
        (state.sms.length && state.sms[0].chatId !== payload.chatId)
      )
        return
      const existing = state.sms.findIndex((item: any) => {
        return item.id === payload.id
      })
      if (existing > -1) {
        state.sms[existing] = { ...state.sms[existing], ...payload }
      } else {
        state.sms = [...state.sms, payload]
      }
    },
    insertChat: (state: SmsState, { payload }: PayloadAction<any>) => {
      const existing = state.chats.findIndex((item: any) => {
        return item.id === payload.id
      })
      const existingChatIds: any = {}
      if (existing > -1) {
        state.chats[existing] = { ...state.chats[existing], ...payload }
        state.chats = sortListByDate(
          state.chats,
          'mostRecentMessageTime',
          true
        ).filter((chat) => {
          if (existingChatIds[chat.chatId]) {
            return false
          }
          existingChatIds[chat.chatId] = chat.id
          return true
        })
      } else {
        state.chats = sortListByDate(
          [...state.chats, payload],
          'mostRecentMessageTime',
          true
        ).filter((chat) => {
          if (existingChatIds[chat.chatId]) {
            return false
          }
          existingChatIds[chat.chatId] = chat.id
          return true
        })
      }
    },
    setCurrentChat: (state, { payload }: PayloadAction<string>) => {
      state.currentChat = payload
    },
    setInitiateChatNumber: (state, { payload }: PayloadAction<string>) => {
      state.initiateChatNumber = payload
    },
    setStars: (state, { payload }: PayloadAction<any>) => {
      state.stars = payload
    },
    setStarLoading: (state, { payload }: PayloadAction<any>) => {
      state.starLoading = payload
    }
  }
})

export const {
  setLoading,
  setErrors,
  setChats,
  setSms,
  setChatRead,
  appendSms,
  insertSms,
  insertChat,
  setCurrentChat,
  setInitiateChatNumber,
  setStars,
  setStarLoading
} = smsSlice.actions
export default smsSlice.reducer
export const smsSelector = (state: { smsStore: SmsState }) => state.smsStore

// AppThunk sets the type definitions for the dispatch method
export const getSms = (
  token: string,
  number: string,
  chatId: string
): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading({ type: 'sms', payload: true }))
    try {
      const res = await HTTP.get(`/sms/${number}/messaging/messages`, {
        headers: {
          Authorization: 'Bearer ' + token
        },
        params: {
          chatId
        }
      })
      const { data } = res.data
      dispatch(setLoading({ type: 'sms', payload: false }))
      dispatch(setSms(data.Items))
    } catch (error) {
      // dispatch(setErrors(error))
      // dispatch(setLoading(false))
    }
  }
}

export const sendSms = (
  token: string,
  number: string,
  toNumber: string,
  message: string,
  media?: Array<any>
): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading({ type: 'sending', payload: true }))
    if (media && media.length) {
      const bodyFormData = new FormData()
      media.forEach((file, index) => bodyFormData.append(`media${index}`, file))
      bodyFormData.append('toNumber', toNumber)
      bodyFormData.append('message', message)
      try {
        const res = await HTTP.post(
          `/sms/${number}/messaging/messages`,
          bodyFormData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: 'Bearer ' + token
            }
          }
        )
        const { data } = res.data
        dispatch(setLoading({ type: 'sending', payload: false }))
        if (data) {
          dispatch(insertSms(data.message))
        }
      } catch (error) {
        console.error(error)
      }
    } else {
      try {
        const res = await HTTP.post(
          `/sms/${number}/messaging/messages`,
          {
            toNumber,
            message,
            media: []
          },
          {
            headers: {
              Authorization: 'Bearer ' + token
            }
          }
        )
        const { data } = res.data
        dispatch(setLoading({ type: 'sending', payload: false }))
        if (data) {
          dispatch(insertSms(data.message))
        }
      } catch (error) {
        // dispatch(setErrors(error))
        // dispatch(setLoading(false))
      }
    }
  }
}

export const getChats = (numbers: any, tokens: any): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading({ type: 'chats', payload: true }))
    const requests = []
    for (const number in numbers) {
      requests.push(
        HTTP.get(`/sms/${number}/messaging/chats`, {
          headers: {
            Authorization: 'Bearer ' + tokens[number]
          }
        })
      )
    }
    Promise.all(requests).then((res: any) => {
      let chats: Array<any> = []
      for (const result of res) {
        const { data } = result.data
        chats = [...chats, ...data]
      }
      dispatch(setChats(chats))
      dispatch(setLoading({ type: 'chats', payload: false }))
    })
  }
}

export const readChat = (
  token: string,
  number: string,
  id: string
): AppThunk => {
  return async (dispatch) => {
    try {
      const res = await HTTP.get(`/sms/${number}/messaging/readChat`, {
        headers: {
          Authorization: 'Bearer ' + token
        },
        params: {
          id
        }
      })
      dispatch(setChatRead(id))
    } catch (error) {
      // dispatch(setErrors(error))
      // dispatch(setLoading(false))
    }
  }
}

export const getStars = (token: string): AppThunk => {
  return async (dispatch) => {
    try {
      const res = await HTTP.get(`/sms/settings/stars`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      dispatch(setStars(res.data.data.stars))
    } catch (e) {
      console.error(e)
    }
  }
}

export const starChat = (
  token: string,
  australisId: string,
  chatId: string
): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState()
    try {
      dispatch(setStarLoading(true))

      const res = await HTTP.post(
        `/sms/settings/${chatId}/star`,
        {
          australisId
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      const { data } = res.data.data

      const newStars = [...state.sms.stars, data]
      dispatch(setStars(newStars))
      dispatch(setStarLoading(false))
    } catch (e) {
      console.error(e)
      dispatch(setStarLoading(false))
    }
  }
}

export const unstarChat = (
  token: string,
  australisId: string,
  chatId: string
): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState()

    try {
      dispatch(setStarLoading(true))

      const res = await HTTP.post(
        `/sms/settings/${chatId}/unstar`,
        { australisId },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      const newStars = state.sms.stars.filter((star) => star.chatId !== chatId)
      dispatch(setStars(newStars))
      dispatch(setStarLoading(false))
    } catch (e) {
      console.error(e)
      dispatch(setStarLoading(false))
    }
  }
}
